import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../ui/utils";

/**
 * Branded button variants following Studio Bereikbaar design system
 * - Primary: Benzol Green (#51BA6D) for primary actions
 * - Secondary: Outlined style for secondary actions
 * - Ghost: Minimal hover effect for tertiary actions
 */
const brandedButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        primary:
          "bg-sb-benzol-green text-white hover:bg-sb-benzol-green-moderate focus-visible:ring-sb-benzol-green/50 shadow-sm",
        secondary:
          "border-2 border-sb-benzol-green bg-white text-sb-benzol-green hover:bg-sb-benzol-green-lightest focus-visible:ring-sb-benzol-green/50",
        ghost:
          "text-sb-black hover:bg-sb-light-grey/50 focus-visible:ring-sb-grey/30",
        destructive:
          "bg-sb-eugenia-red text-white hover:bg-sb-eugenia-red-moderate focus-visible:ring-sb-eugenia-red/50 shadow-sm",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 text-xs has-[>svg]:px-2.5",
        lg: "h-12 rounded-md px-6 text-base has-[>svg]:px-4",
        icon: "size-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface BrandedButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof brandedButtonVariants> {
  /**
   * Render as a child component (e.g., Link)
   */
  asChild?: boolean;
}

/**
 * Branded Button Component
 *
 * A button component that follows Studio Bereikbaar's design system with
 * Benzol Green as the primary color and consistent styling across variants.
 *
 * @example
 * ```tsx
 * // Primary action button
 * <BrandedButton>Save Changes</BrandedButton>
 *
 * // Secondary action
 * <BrandedButton variant="secondary">Cancel</BrandedButton>
 *
 * // Ghost button for minimal emphasis
 * <BrandedButton variant="ghost">Learn More</BrandedButton>
 *
 * // Large button
 * <BrandedButton size="lg">Get Started</BrandedButton>
 *
 * // As a Link component
 * <BrandedButton asChild>
 *   <Link to="/dashboard">Go to Dashboard</Link>
 * </BrandedButton>
 * ```
 */
export function BrandedButton({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: BrandedButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      className={cn(brandedButtonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
